import { useState, useCallback } from 'react'
import { IStockProductSearchResult } from '../../../../../types'
import productApi from '../../../../../api/productApi'
import { useApiClient } from '../../../../../context/AppContext'

interface ProductLookupState {
  lookupField: string
  productNotFoundError: string
  isScanning: boolean
}

export const useProductLookup = (storeLocationId: string) => {
  const apiClient = useApiClient()
  
  const [state, setState] = useState<ProductLookupState>({
    lookupField: '',
    productNotFoundError: '',
    isScanning: false,
  })

  const setLookupField = (value: string) => {
    setState(prev => ({ ...prev, lookupField: value }))
  }

  const setIsScanning = (value: boolean) => {
    setState(prev => ({ ...prev, isScanning: value }))
  }

  const resetState = useCallback(() => {
    setState({
      lookupField: '',
      productNotFoundError: '',
      isScanning: false,
    })
  }, [])

  const lookupProduct = useCallback(async (query: string): Promise<IStockProductSearchResult | null> => {
    if (!query) {
      setState(prev => ({
        ...prev,
        productNotFoundError: 'Please enter a product name, SKU or EAN to search.'
      }))
      return null
    }

    try {
      const response = await productApi.SearchProduct(apiClient, query, storeLocationId, 1)
      
      // Ensure response has expected structure
      if (!response || typeof response !== 'object') {
        setState(prev => ({
          ...prev,
          productNotFoundError: 'An unexpected error occurred. Please try again.'
        }))
        return null
      }

      // Safely access products array with fallback to empty array
      const products = Array.isArray(response.products) ? response.products : []
      
      if (products.length === 0) {
        setState(prev => ({
          ...prev,
          productNotFoundError: `No products found for "${query}". Please try a different search term or enter the details manually.`
        }))
        return null
      }

      // Clear error if we got results
      setState(prev => ({
        ...prev,
        productNotFoundError: ''
      }))
      
      // Return validated response with safe defaults
      return {
        products,
        totalResults: response.totalResults ?? 0,
        totalPages: response.totalPages ?? 0,
        currentPage: response.currentPage ?? 1,
        pageSize: response.pageSize ?? 0,
        query: response.query,
        storeCode: response.storeCode
      }
    } catch (error) {
      // Catch any unexpected errors and show user-friendly message
      setState(prev => ({
        ...prev,
        productNotFoundError: 'An error occurred while searching for products. Please try again.'
      }))
      return null
    }
  }, [apiClient, storeLocationId])

  return {
    ...state,
    setLookupField,
    setIsScanning,
    lookupProduct,
    resetState,
  }
}

export default useProductLookup
